import { Component, OnInit } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { Store } from '@ngrx/store'
import { combineLatest, Observable } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  take
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { settingSelectors } from 'src/app/core/settings/settings.selectors'
import { configurationOptionSelectors } from 'src/app/features/configuration-option/configuration-option.selectors'
import { environment } from 'src/environments/environment'
import { ConfigurationService } from '../../shared/services/configuration.service'

import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { ConfigOptionsKeys } from '../../shared/constants'

interface BrandedLayoutConfig {
  showFooterInMobileApp: boolean
  showPublicHeader: boolean
}

@Component({
  selector: 'branded-page-layout',
  templateUrl: './branded-page-layout.component.html',
  styleUrls: ['./branded-page-layout.component.scss']
})
export class BrandedPageLayoutComponent implements OnInit {
  public termsConditionsHTML$: Observable<SafeHtml>
  public landingPageBrandingContent$: Observable<SafeHtml>
  public primaryWhiteLogoUrl$: Observable<string>
  public primaryLogoUrl$: Observable<string>
  public showFooter$: Observable<boolean>
  public showPublicHeader$: Observable<boolean>

  public configIsLoading$ = this.store.select(
    configurationOptionSelectors.selectIsLoading
  )

  constructor(
    private store: Store<AppState>,
    private sanitizer: DomSanitizer,
    public configurationService: ConfigurationService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit() {
    const layoutConfig$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(null),
      map(() => this.currentChildRouteLayoutConfig()),
      distinctUntilChanged(
        (previous, current) =>
          previous.showFooterInMobileApp === current.showFooterInMobileApp &&
          previous.showPublicHeader === current.showPublicHeader
      )
    )

    this.showFooter$ = combineLatest([
      this.configurationService.isMobileApp$,
      layoutConfig$
    ]).pipe(
      map(([isMobileApp, layoutConfig]) => {
        return isMobileApp && layoutConfig.showFooterInMobileApp
      }),
      distinctUntilChanged()
    )

    this.showPublicHeader$ = layoutConfig$.pipe(
      map((layoutConfig) => layoutConfig.showPublicHeader),
      distinctUntilChanged()
    )

    this.primaryWhiteLogoUrl$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.PRIMARY_WHITE_LOGO_IMAGE_PATH
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((configOption) =>
          configOption?.value.length
            ? `${environment.staticAssetBaseUrl}${configOption?.value}`
            : ''
        )
      )
    this.primaryLogoUrl$ = this.store
      .select(
        configurationOptionSelectors.selectByKey,
        ConfigOptionsKeys.PRIMARY_LOGO_IMAGE_PATH
      )
      .pipe(
        filter((x) => Boolean(x)),
        map((configOption) =>
          configOption?.value.length
            ? `${environment.staticAssetBaseUrl}${configOption?.value}`
            : ''
        )
      )

    this.termsConditionsHTML$ = this.store
      .select(settingSelectors.selectById, 'language')
      .pipe(
        switchMap((language) =>
          this.store
            .select(
              configurationOptionSelectors.selectByKey,
              `${language}_terms_conditions_footer`
            )
            .pipe(
              filter((configurationOption) => Boolean(configurationOption)),
              map((configurationOption) =>
                this.sanitizer.bypassSecurityTrustHtml(
                  configurationOption?.value
                )
              )
            )
        )
      )

    this.landingPageBrandingContent$ = this.store
      .select(settingSelectors.selectById, 'language')
      .pipe(
        switchMap((language) =>
          this.store
            .select(
              configurationOptionSelectors.selectByKey,
              `${language}_landing_page_branding`
            )
            .pipe(
              filter((configurationOption) => Boolean(configurationOption)),
              map((configurationOption) =>
                this.sanitizer.bypassSecurityTrustHtml(
                  configurationOption?.value
                )
              )
            )
        )
      )
  }

  private currentChildRouteLayoutConfig(): BrandedLayoutConfig {
    let route = this.route
    let showFooterInMobileApp =
      route.snapshot.data.layout?.showFooterInMobileApp === true
    let showPublicHeader = route.snapshot.data.layout?.showPublicHeader === true

    while (route.firstChild) {
      route = route.firstChild
      showFooterInMobileApp =
        showFooterInMobileApp ||
        route.snapshot.data.layout?.showFooterInMobileApp === true
      showPublicHeader =
        showPublicHeader ||
        route.snapshot.data.layout?.showPublicHeader === true
    }

    return {
      showFooterInMobileApp,
      showPublicHeader
    }
  }

  public showBlankPage$(): Observable<boolean> {
    return this.route.queryParams.pipe(
      take(1),
      map((queryParams) => {
        return Boolean(queryParams?.surveyId)
      })
    )
  }
}
